/**
 * `LearningJourney`, top-level orchestrator of a player's progression
 * through a song.
 *
 * Pure TypeScript: no React, no Studio, no DOM imports. The class
 * composes:
 *
 *   - one `PhaseFSM` for the global phase (phase1 / phase2 / repertoire),
 *   - one `PhraseFSM` per phrase (S0 → DONE),
 *   - one `PerformanceCapture` to ingest piano/beat events for the
 *     detectors,
 *   - one `ProgressStore` to persist the progression across sessions.
 *
 * The orchestrator does **not** drive playback nor evaluate runs, those
 * concerns belong to `RangeRunner` and the detectors. It exposes
 * commands that the UI bridge (chantier 7) translates into FSM events,
 * and a snapshot consumed via `subscribe`.
 *
 * ## Routing
 *
 *   - Piano press / release / beat events are forwarded to the
 *     `PerformanceCapture`. Detectors query that capture; this class
 *     does not interpret the timed stream itself.
 *   - State-shaping commands (`acknowledgePresentChord`, `confirmPosition`,
 *     `completePracticeRun`, …) are dispatched to the *current* phrase
 *     FSM. The "current phrase" is the first phrase in `phrases[]` whose
 *     FSM state is not `DONE`, it auto-advances when a phrase resolves.
 *
 * ## Phase auto-transition policy
 *
 *   - `phase1 → phase2` is triggered automatically when every phrase
 *     reaches `DONE`. This is desirable: the user has nothing meaningful
 *     to do at that boundary, the FSM cannot drift back, and the UI can
 *     simply react to the snapshot change.
 *   - `phase2 → repertoire` is NOT automatic in the same sense: it needs an
 *     explicit `enterRepertoire()` from the UI, because the fact that a
 *     traversal held is read off a run — the tempo it was played at, the
 *     stops, the share of attacks answered — and none of that reaches the
 *     orchestrator on its own.
 *
 * ## Persistence
 *
 *   - At construction, the orchestrator calls
 *     `progressStore.load(morceauId, phrasePlanHash)`. If the result is
 *     non-null, each `PhraseFSM` is fast-forwarded to its persisted
 *     `etatMax` via `PhraseFSM.fastForwardTo`.
 *   - After every effective mutation, `saveProgress()` is invoked. The
 *     store is expected to be cheap enough that no debouncing is needed
 *     (writes are synchronous to a Storage-shaped backend; volume is
 *     low, at most a few writes per phrase). If we observe contention
 *     in production, debounce-on-microtask can be added in one place
 *     without touching callers.
 */

import { PhaseFSM, type Phase } from './phase-fsm';
import { PhraseFSM } from './phrase-fsm';
import {
  PROGRESS_SCHEMA_VERSION,
  type JourneyProgress,
  type PhraseProgress,
  type ProgressStore,
  type SectionProgress,
  type TraversalProgress,
} from './progress-store';
import { PerformanceCapture } from './runtime/performance-capture';
import type {
  PerformanceCaptureQuery,
  Phrase,
  PhraseFSMSnapshot,
  PhraseState,
  RecordedBeat,
  RunOutcome,
} from './types';

// ── Public types ──────────────────────────────────────────────────

/**
 * Immutable view of the journey state at one point in time. A new
 * object is produced on every mutation; consumers that compare by
 * reference (React `useState`/`useMemo`) get reliable change detection.
 *
 * `capture` is exposed as a read-only `PerformanceCaptureQuery` so
 * detectors and the UI can interrogate the timed stream without us
 * leaking the writable `PerformanceCapture` instance. Callers that
 * truly need the writable surface own the orchestrator and can use
 * the dedicated `record*` methods.
 */
export type JourneySnapshot = {
  readonly phase: Phase;
  readonly currentPhraseIndex: number;
  readonly currentPhrase: Phrase | null;
  readonly currentPhraseSnapshot: PhraseFSMSnapshot | null;
  /**
   * Unit whose parcours the player re-opened from the piece screen, or
   * `null` when the screen on show is the one the phase asks for.
   *
   * Always `null` in phase 1, where the parcours *is* the phase and there
   * is nothing to return from. See `revisitUnit`.
   */
  readonly revisitingUnit: number | null;
  readonly phraseProgress: readonly PhraseProgress[];
  readonly tempoSpeedupCourant: number | null;
  readonly capture: PerformanceCaptureQuery;
};

export type LearningJourneyOptions = {
  readonly morceauId: string;
  readonly phrases: readonly Phrase[];
  readonly phrasePlanHash: string;
  readonly progressStore: ProgressStore;
  /** Defaults to `Date.now`. */
  readonly now?: () => number;
};

// ── Implementation ────────────────────────────────────────────────

export class LearningJourney {
  private readonly morceauId: string;
  private readonly phrases: readonly Phrase[];
  private readonly phrasePlanHash: string;
  private readonly progressStore: ProgressStore;
  /**
   * Wall clock, injectable so the dated parts of the progression can be
   * exercised without freezing global time in the tests.
   */
  private readonly now: () => number;

  private readonly phaseFsm: PhaseFSM;
  private readonly phraseFsms: readonly PhraseFSM[];
  /**
   * Unit whose parcours is open on top of the piece screen. See
   * `revisitUnit`. Deliberately not persisted: it is where the player is
   * looking, not what they have learned.
   */
  private revisitingUnit: number | null = null;
  private readonly capture: PerformanceCapture;

  private currentPhraseIndex = 0;
  private tempoSpeedupCourant: number | null = null;
  /**
   * Fastest tempo, as a fraction of the written one, each unit has actually
   * been played through at. Indexed like `phrases`; `0` means never.
   *
   * This is what S4 hands to S5, and what a returning player finds instead of
   * a constant: the chaining step used to pin itself back to a global 0.6,
   * sending someone who had to drop to 0.4 to hold a dense unit straight back
   * to a tempo they never held, on the passage they know least.
   */
  private phraseRates: number[];
  /** Epoch ms each unit was last played through. `0` means never. */
  private phrasePlayedAt: number[];
  /** The last crossing of the whole piece, and what it saw. */
  private lastTraversalRead: TraversalProgress | null = null;
  private cachedSnapshot: JourneySnapshot | null = null;

  private listeners = new Set<(snapshot: JourneySnapshot) => void>();

  constructor(opts: LearningJourneyOptions) {
    this.morceauId = opts.morceauId;
    this.phrases = opts.phrases;
    this.phrasePlanHash = opts.phrasePlanHash;
    this.progressStore = opts.progressStore;

    this.phaseFsm = new PhaseFSM('phase1');
    // Only the first unit has nothing before it to be joined to.
    this.phraseFsms = opts.phrases.map(
      (phrase, index) => new PhraseFSM(phrase, { chains: index > 0 }),
    );
    this.phraseRates = opts.phrases.map(() => 0);
    this.phrasePlayedAt = opts.phrases.map(() => 0);
    this.now = opts.now ?? Date.now;
    this.capture = new PerformanceCapture();

    this.hydrateFromStore();
  }

  // ── Static accessors ────────────────────────────────────────────

  /**
   * Read-only access to the phrase plan this journey was built with.
   * Useful for views that need neighbouring phrases (e.g. S5 chains
   * phrase N-1 → N) without rebuilding the plan from the song.
   */
  getPhrases(): readonly Phrase[] {
    return this.phrases;
  }

  // ── Snapshot ────────────────────────────────────────────────────

  getSnapshot(): JourneySnapshot {
    if (this.cachedSnapshot === null) {
      this.cachedSnapshot = this.buildSnapshot();
    }

    return this.cachedSnapshot;
  }

  subscribe(cb: (snapshot: JourneySnapshot) => void): () => void {
    this.listeners.add(cb);

    return () => {
      this.listeners.delete(cb);
    };
  }

  // ── Capture forwarding ──────────────────────────────────────────

  recordPianoPress(midi: number, tMs: number, velocity?: number): void {
    this.capture.recordPress(midi, tMs, velocity);
    // Capture mutations don't change the FSM state, no notify, no save.
    // The detectors poll the capture on demand.
  }

  recordPianoRelease(midi: number, tMs: number): void {
    this.capture.recordRelease(midi, tMs);
  }

  recordBeat(beat: RecordedBeat): void {
    this.capture.recordBeat(beat);
  }

  /**
   * Empty the performance capture, before the pass is under way.
   *
   * Called when the pass is **armed**, not when the playhead first moves.
   * Clearing on the first tick threw away the note the player was asked to
   * play *on* that tick: presses are timestamped continuously with
   * `performance.now()`, the anchor is pushed forward by the output latency,
   * and a downbeat played on time — or a few milliseconds early, which is
   * what playing in time looks like — landed before the callback that wiped
   * the buffer. The first onset came back unhonoured while every other one
   * read correctly.
   *
   * Presses made during the count-in survive this, and that is harmless:
   * `honouredOnsets` only looks within a beat of each onset, and
   * `findInterruptions` ignores anything before the anchor.
   *
   * Capture writes are intentionally not notified to FSM listeners.
   */
  armRunCapture(): void {
    this.capture.clear();
  }

  /**
   * Anchor the run-start instant, once the playhead has actually moved and
   * the reference frame is known. Does not clear — see `armRunCapture`.
   */
  beginRunCapture(tMs: number): void {
    this.capture.markRunStart(tMs);
  }

  // ── PhraseFSM commands (current phrase) ─────────────────────────

  acknowledgePresentChord(): void {
    this.dispatchToCurrentPhrase({ type: 'presentChordAcknowledged' });
  }

  confirmPosition(): void {
    this.dispatchToCurrentPhrase({ type: 'positionConfirmed' });
  }

  /**
   * A run without rhythm finished.
   *
   * `pace` is the tempo it was played at, as a fraction of the written one.
   * Only the two-hand practice sends one, and it is what the run in time
   * opens at: the player meets the passage in time at the speed they have
   * just shown they can play it. See `naturalRate` in `mastery.ts`.
   */
  completePracticeRun(outcome: RunOutcome, pace?: number): void {
    this.dispatchToCurrentPhrase({
      type: 'practiceRunCompleted',
      outcome,
      pace,
    });
  }

  /**
   * The player leaves the current hand's steps — grip or run — without doing
   * them.
   *
   * Records no run. See `PhraseFSMSnapshot.handSkipTarget`.
   */
  skipHand(): void {
    this.dispatchToCurrentPhrase({ type: 'handSkipped' });
  }

  /**
   * The player switches the two-hand step into its **timed** régime.
   *
   * `pace` is the tempo the free run measured, when it measured one. The one
   * door into the metronome: the free run no longer hands over on its own.
   * See `PhraseFSM`, « S3 and S4 are one step in two régimes ».
   */
  enterTime(pace?: number): void {
    this.dispatchToCurrentPhrase({ type: 'enterTime', pace });
  }

  /**
   * The player switches it back to its **free** régime.
   *
   * Free and repeatable, and the tempo it measured survives the round trip.
   */
  leaveTime(): void {
    this.dispatchToCurrentPhrase({ type: 'leaveTime' });
  }

  startRhythmRun(): void {
    this.dispatchToCurrentPhrase({ type: 'runStarted' });
  }

  completeRhythmRun(outcome: RunOutcome): void {
    const fsm = this.getCurrentPhraseFsm();

    if (fsm === null) {
      return;
    }

    const state = fsm.getSnapshot().state;

    if (state === 'S4') {
      this.dispatchToCurrentPhrase({ type: 'rhythmRunCompleted', outcome });

      return;
    }

    if (state === 'S5') {
      this.dispatchToCurrentPhrase({ type: 'chainRunCompleted', outcome });
    }
  }

  /**
   * One sub-step back inside the current unit.
   *
   * Not one segment of the stepper: the five segments fold nine states, so
   * « retour » from the two-hand practice used to land on the right hand's
   * grip and cost the player everything in between. See
   * `PhraseFSM.previousState`.
   */
  stepBack(): void {
    this.dispatchToCurrentPhrase({ type: 'stepBack' });
  }

  /**
   * Remember that the current unit was played through at `rate`.
   *
   * Kept as the **fastest** the unit has held, so a deliberate slow pass
   * after a fast one does not erase what the player can do. S5 takes the
   * slower of the two units it joins.
   *
   * `at` names a unit other than the current one: phase 2 has no current
   * phrase, and a traversal that held is evidence for **every** unit it
   * crossed — otherwise a passage would stay on the "slower than the piece"
   * list after being played through inside it.
   */
  recordUnitRate(rate: number, at?: number): void {
    const index = at ?? this.currentPhraseIndex;

    if (index < 0 || index >= this.phraseRates.length || rate <= 0) {
      return;
    }

    if (rate <= this.phraseRates[index]) {
      return;
    }

    this.phraseRates[index] = rate;
    this.phrasePlayedAt[index] = this.now();
    this.invalidateAndPersist();
  }

  /** The fastest tempo a unit has been played through at, or `null`. */
  unitRate(index: number): number | null {
    const rate = this.phraseRates[index];

    return rate === undefined || rate <= 0 ? null : rate;
  }

  /** When a unit was last played through, in epoch ms, or `null`. */
  unitPlayedAt(index: number): number | null {
    const at = this.phrasePlayedAt[index];

    return at === undefined || at <= 0 ? null : at;
  }

  /**
   * Remember what a crossing of the whole piece saw.
   *
   * Only a crossing of the *whole* piece: the rail reports on the piece, and
   * a section played on its own says nothing about the parts it did not
   * touch. Overwriting the map with a partial one would show a piece as
   * unread everywhere except the one part just played.
   */
  recordTraversal(
    sections: readonly SectionProgress[],
    rate: number,
    held: boolean,
  ): void {
    this.lastTraversalRead = {
      at: this.now(),
      sections: [...sections],
      rate,
      held,
    };
    this.invalidateAndPersist();
  }

  /** The last crossing of the whole piece, or `null`. */
  lastTraversal(): TraversalProgress | null {
    return this.lastTraversalRead;
  }

  /**
   * Switch the active phrase index — « Phrase suivante » on a validated unit.
   *
   * The unit is taken up from its first step: coming to it from another one
   * is starting it, not resuming it. What it had reached is kept — see
   * `arriveAt`.
   *
   * No-op when the index is out of range or already current.
   */
  goToPhrase(target: number): void {
    if (target < 0 || target >= this.phraseFsms.length) {
      return;
    }

    if (target === this.currentPhraseIndex) {
      return;
    }

    this.arriveAt(target);

    // A visit follows the player from unit to unit — "phrase suivante" on a
    // validated unit is still a visit, not a return to the piece.
    if (this.revisitingUnit !== null) {
      this.revisitingUnit = target;
    }

    this.invalidateAndPersist();
  }

  /**
   * Re-open a unit's parcours, from wherever the player is.
   *
   * In phase 1 this is `goToPhrase` under another name: the parcours is
   * already the screen. **After it, it is a visit.** The piece has been
   * through phase 1, it may be in the repertoire, and going back to work
   * bar 3 must not undo any of that — so the phase does not move, the unit
   * keeps its own state, and `endRevisit` hands the player back to the
   * piece. This is the same arbitration the phase FSM makes when it says a
   * passage is re-opened "without changing phase"; what it did not have was
   * a way to re-open the *whole* parcours of a unit rather than its run in
   * time alone.
   *
   * No-op when the index is out of range.
   */
  revisitUnit(index: number): void {
    if (index < 0 || index >= this.phraseFsms.length) {
      return;
    }

    if (this.phaseFsm.getPhase() !== 'phase1') {
      this.revisitingUnit = index;
      this.currentPhraseIndex = index;
    } else if (index !== this.currentPhraseIndex) {
      // In phase 1 this is a change of unit, from the one on screen.
      this.arriveAt(index);
    }

    // Notify, don't persist: neither the index nor the visit is part of the
    // saved record, so a write here is a round-trip to the server that
    // changes nothing — and the chip offers this on every screen now.
    this.invalidateAndNotify();
  }

  /** Close the visit and hand the player back to the piece screen. */
  endRevisit(): void {
    if (this.revisitingUnit === null) {
      return;
    }

    this.revisitingUnit = null;
    // Nothing persisted changed: a visit is navigation, not progress.
    this.invalidateAndNotify();
  }

  /**
   * User-driven jump back to a previously visited phrase state, used by
   * the stepper UI ("revoir l'étape"). Forwards to
   * `PhraseFSM.fastForwardTo`, which clamps to a legal target and
   * preserves `maxState` so the stepper keeps later steps clickable.
   *
   * No-op outside phase1 / when no current phrase is active.
   */
  revisitPhraseState(target: PhraseState): void {
    const fsm = this.getCurrentPhraseFsm();

    if (!fsm) {
      return;
    }

    const before = fsm.getSnapshot().state;
    fsm.fastForwardTo(target);
    const after = fsm.getSnapshot().state;

    if (before === after) {
      return;
    }

    this.invalidateAndPersist();
  }

  // ── Phase commands ──────────────────────────────────────────────

  /**
   * The tempo the piece is being played at in phase 2, as a fraction of the
   * written one.
   *
   * It was `selectSpeedupTier`, and it took one of three fixed values. There
   * are no tiers any more: **70.3% of units open below the lowest of them**,
   * and the real climb is nine rungs of `RATE_LADDER`. What is stored is a
   * rate, and it is the piece's — see `components/learn-piece/piece.ts`.
   */
  setPieceRate(rate: number): void {
    if (this.tempoSpeedupCourant === rate) {
      return;
    }

    this.tempoSpeedupCourant = rate;
    this.invalidateAndPersist();
  }

  /**
   * The piece has been played through end to end. Triggers
   * `phase2 → repertoire`.
   *
   * It used to be `reachTargetTempo`, and it was never called: no player
   * could reach the phase it opened. It is now dispatched by the piece
   * screen itself, on the reading it already computes.
   */
  enterRepertoire(): void {
    const before = this.phaseFsm.getPhase();
    this.phaseFsm.playedThrough();
    this.notifyIfPhaseChanged(before);
  }

  // ── Persistence ─────────────────────────────────────────────────

  saveProgress(): void {
    const progress = this.buildPersistedProgress();
    this.progressStore.save(this.morceauId, progress);
  }

  /**
   * Resets the journey to its initial state and clears the persisted
   * entry. Mainly useful for tests and dev tools, no UI affordance is
   * planned in V1.
   */
  reset(): void {
    this.progressStore.clear(this.morceauId);
    this.phaseFsm.reset();
    this.phraseFsms.forEach((fsm) => {
      fsm.forget();
    });
    this.currentPhraseIndex = 0;
    this.revisitingUnit = null;
    this.tempoSpeedupCourant = null;
    this.capture.clear();

    this.invalidateAndNotify();
    // No save: reset means "discard everything", not "persist a fresh
    // empty state". The next legitimate mutation will write a clean
    // record back.
  }

  // ── Internal: routing ───────────────────────────────────────────

  private dispatchToCurrentPhrase(event: PhraseEventLike): void {
    const fsm = this.getCurrentPhraseFsm();

    if (fsm === null) {
      return;
    }

    const beforeState = fsm.getSnapshot().state;
    const mutated = fsm.dispatch(event);

    // Un événement ignoré ne vaut ni instantané neuf, ni rendu, ni écriture
    // dans `localStorage`, ni poussée serveur.
    if (!mutated) {
      return;
    }

    const afterState = fsm.getSnapshot().state;

    const stateChanged = beforeState !== afterState;

    // A visit stays on its unit. Walking to the next one is what phase 1
    // does with a validated unit; here the player came from the piece and
    // that is where they go back to, by their own act.
    if (afterState === 'DONE' && this.revisitingUnit === null) {
      this.advanceToNextUnit();
    }

    if (stateChanged && this.phaseFsm.getPhase() === 'phase1') {
      this.maybeAutoAdvancePhase();
    }

    this.invalidateAndPersist();
  }

  /**
   * Make `index` the current unit, taken up from its first step.
   *
   * A unit the player comes to from another one starts at `S0`, whatever it
   * reached before: the grip and the practice are where a passage is picked
   * up, and landing on its run in time — or its « bravo » — was resuming a
   * session the player had left on another passage.
   *
   * Nothing is forgotten: `maxState` keeps what was reached, so the stepper
   * still opens those steps, and the progress saved is that maximum — a unit
   * started over is still a validated unit. See `buildPhraseProgressSnapshot`.
   */
  private arriveAt(index: number): void {
    this.currentPhraseIndex = index;
    this.phraseFsms[index]?.fastForwardTo('S0');
  }

  private getCurrentPhraseFsm(): PhraseFSM | null {
    if (this.currentPhraseIndex >= this.phraseFsms.length) {
      return null;
    }

    return this.phraseFsms[this.currentPhraseIndex];
  }

  /**
   * Walks to the unit after the one just validated.
   *
   * One step, and not "to the first unit still to be worked". On the way up
   * the two are the same thing — the unit after the one being worked is
   * never already validated — so the difference only shows on a **return**:
   * the player scrolled back to bar 3 of a piece worked up to bar 12,
   * re-opened its parcours and played it through. Sending them back to bar
   * 12 there answers a question they did not ask, one run after they chose
   * the passage themselves, and it ends the re-reading at its first unit.
   * What follows bar 3 is bar 4; its panel offers both acts — working it
   * again, or going back to the front — and the player says which.
   *
   * Past the last unit there is no "after". Anything left behind on the way
   * is where they go; otherwise the index sits one past the end, which is
   * what tells `maybeAutoAdvancePhase` the piece is through.
   */
  private advanceToNextUnit(): void {
    const next = this.currentPhraseIndex + 1;

    if (next < this.phraseFsms.length) {
      this.arriveAt(next);

      return;
    }

    const pending = this.phraseFsms.findIndex((fsm) => !isValidated(fsm));

    if (pending < 0) {
      this.currentPhraseIndex = next;

      return;
    }

    this.arriveAt(pending);
  }

  /**
   * Advances `currentPhraseIndex` past every contiguous DONE phrase.
   * Done in a loop because hydration may resolve several DONE phrases
   * at once (e.g. user finished phrase 0 and 1, app reload).
   */
  private advancePastDonePhrases(): void {
    while (this.currentPhraseIndex < this.phraseFsms.length) {
      const fsm = this.phraseFsms[this.currentPhraseIndex];

      if (!isValidated(fsm)) {
        return;
      }

      this.currentPhraseIndex += 1;
    }
  }

  /**
   * If every phrase is DONE while we are in phase1, auto-transition to
   * phase2. This is the only auto-phase-transition; later phases require
   * explicit user input (tempo choice, performance completion).
   */
  private maybeAutoAdvancePhase(): void {
    if (this.currentPhraseIndex < this.phraseFsms.length) {
      return;
    }

    if (this.phraseFsms.length === 0) {
      return;
    }

    if (this.phaseFsm.getPhase() !== 'phase1') {
      return;
    }

    // Said outright rather than inferred from the index: the phase never
    // comes back, so the one thing it waits for is worth reading off the
    // units themselves.
    if (this.phraseFsms.some((fsm) => !isValidated(fsm))) {
      return;
    }

    this.phaseFsm.allPhrasesValidated();
  }

  // ── Internal: hydration ─────────────────────────────────────────

  private hydrateFromStore(): void {
    const stored = this.progressStore.load(this.morceauId, this.phrasePlanHash);

    if (stored === null) {
      return;
    }

    this.tempoSpeedupCourant = stored.tempoSpeedupCourant;

    // Hydrate each phrase FSM by id. Missing entries are silently kept
    // at S0, the persisted plan and the current plan share the same
    // hash by construction (validated by `progressStore.load`), so a
    // mismatch here would be a bug, not a user-recoverable state.
    const byId = new Map<string, PhraseProgress>();
    for (const entry of stored.phrases) {
      byId.set(entry.id, entry);
    }

    for (let i = 0; i < this.phrases.length; i += 1) {
      const phrase = this.phrases[i];
      const entry = byId.get(phrase.id);

      if (entry === undefined) {
        continue;
      }

      this.phraseFsms[i].fastForwardTo(entry.etatMax);
      this.phraseRates[i] = entry.tempoValideMax;
      this.phrasePlayedAt[i] = entry.lastPlayedAt;
    }

    this.lastTraversalRead = stored.lastTraversal;

    this.advancePastDonePhrases();

    // Restore the global phase last so the auto-advance check above does
    // not double-fire. We trust the persisted phase: the store guards
    // against drift via the plan hash.
    this.applyPersistedPhase(stored.phaseGlobale);
  }

  private applyPersistedPhase(target: Phase): void {
    // PhaseFSM only allows linear forward transitions, so we walk it
    // step-by-step. Going backwards is not expected from a persisted
    // payload; if the data is corrupt we land on the highest reachable
    // phase, which is the safe fallback.
    const order: Phase[] = ['phase1', 'phase2', 'repertoire'];
    const targetIdx = order.indexOf(target);

    if (targetIdx < 0) {
      return;
    }

    while (this.phaseFsm.getPhase() !== target) {
      const currentIdx = order.indexOf(this.phaseFsm.getPhase());

      if (currentIdx >= targetIdx) {
        return;
      }

      const next = order[currentIdx + 1];

      if (next === 'phase2') {
        this.phaseFsm.allPhrasesValidated();
      } else if (next === 'repertoire') {
        this.phaseFsm.playedThrough();
      }
    }
  }

  // ── Internal: snapshot & notify ────────────────────────────────

  private buildSnapshot(): JourneySnapshot {
    const fsm = this.getCurrentPhraseFsm();
    const phase = this.phaseFsm.getPhase();
    const revisiting = this.revisitingUnit !== null;
    const showCurrentPhrase = (phase === 'phase1' || revisiting) && fsm !== null;

    return {
      phase,
      currentPhraseIndex: this.currentPhraseIndex,
      currentPhrase: showCurrentPhrase ? this.phrases[this.currentPhraseIndex] : null,
      currentPhraseSnapshot: showCurrentPhrase ? fsm.getSnapshot() : null,
      revisitingUnit: showCurrentPhrase ? this.revisitingUnit : null,
      phraseProgress: this.buildPhraseProgressSnapshot(),
      tempoSpeedupCourant: this.tempoSpeedupCourant,
      capture: this.capture,
    };
  }

  private buildPhraseProgressSnapshot(): PhraseProgress[] {
    return this.phrases.map((phrase, idx) => ({
      id: phrase.id,
      // The furthest the unit went, not where it sits: a unit started over
      // from another one is back on `S0` and still validated. See `arriveAt`.
      etatMax: this.phraseFsms[idx].getSnapshot().maxState,
      tempoValideMax: this.phraseRates[idx],
      lastPlayedAt: this.phrasePlayedAt[idx],
    }));
  }

  private buildPersistedProgress(): JourneyProgress {
    return {
      schemaVersion: PROGRESS_SCHEMA_VERSION,
      phrasePlanHash: this.phrasePlanHash,
      morceauId: this.morceauId,
      phrases: this.buildPhraseProgressSnapshot(),
      phaseGlobale: this.phaseFsm.getPhase(),
      tempoSpeedupCourant: this.tempoSpeedupCourant,
      lastTraversal: this.lastTraversalRead,
    };
  }

  private invalidateAndNotify(): void {
    this.cachedSnapshot = null;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((cb) => {
      cb(snapshot);
    });
  }

  private invalidateAndPersist(): void {
    this.invalidateAndNotify();
    this.saveProgress();
  }

  private notifyIfPhaseChanged(before: Phase): void {
    if (this.phaseFsm.getPhase() === before) {
      return;
    }

    this.invalidateAndPersist();
  }
}

// ── Local alias ───────────────────────────────────────────────────

/**
 * Local re-import to keep the dispatchToCurrentPhrase signature simple
 * without re-exporting `PhraseEvent` from the orchestrator.
 */
type PhraseEventLike = Parameters<PhraseFSM['dispatch']>[0];

/** A unit that has been all the way through, even if started over since. */
function isValidated(fsm: PhraseFSM): boolean {
  return fsm.getSnapshot().maxState === 'DONE';
}
